'use client';

import { useEffect } from 'react';
import dynamic from 'next/dynamic';

const ModelViewer = dynamic(
  () => import('./ModelViewer.jsx'),
  { ssr: false }
);

export default function ModelDetailModal({ model, isOpen, onClose }) {
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
    }
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen || !model) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-dark-2 rounded-2xl shadow-2xl max-w-6xl w-full min-h-[70vh] max-h-[90vh] overflow-y-auto animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-dark-2 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <span className="inline-block text-xs font-semibold text-accent bg-accent/10 px-3 py-1 rounded-full mb-2">
              {model.category}
            </span>
            <h2 className="text-2xl font-heading font-bold">{model.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 dark:bg-dark-1 hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: 3D Viewer */}
            <div>
              <div className="aspect-square bg-gray-100 dark:bg-dark-1 rounded-xl overflow-hidden mb-4">
                <ModelViewer
                  src={model.file}
                  alt={model.title}
                  ar={model.arEnabled}
                  cameraControls={true}
                  autoRotate={model.autoRotate}
                />
              </div>

              {/* AR Info */}
              {model.arEnabled && (
                <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 flex items-start gap-3">
                  <div className="w-8 h-8 bg-accent text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm mb-1">AR-Ansicht verfügbar</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Auf iOS/Android: Tippen Sie auf das AR-Symbol im 3D-Viewer, um das Modell in Ihrer Umgebung zu platzieren.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Details */}
            <div>
              {/* Description */}
              <div className="mb-6">
                <h3 className="text-lg font-heading font-bold mb-3">Beschreibung</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {model.description}
                </p>
              </div>

              {/* Specs */}
              <div className="mb-6">
                <h3 className="text-lg font-heading font-bold mb-3">Technische Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-dark-1 rounded-lg p-4">
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Dateigröße</div>
                    <div className="font-semibold">{model.fileSize}</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-dark-1 rounded-lg p-4">
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Polygonanzahl</div>
                    <div className="font-semibold">{model.polyCount}</div>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="mb-6">
                <h3 className="text-lg font-heading font-bold mb-3">Features</h3>
                <ul className="space-y-2">
                  {model.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-accent flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Tags */}
              <div className="mb-6">
                <h3 className="text-lg font-heading font-bold mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {model.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="text-sm bg-gray-100 dark:bg-dark-1 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-primary text-white rounded-xl p-6">
                <h3 className="text-xl font-heading font-bold mb-2">Interessiert?</h3>
                <p className="text-gray-200 mb-4">
                  Benötigen Sie ein ähnliches 3D-Modell für Ihr Projekt?
                </p>
                <a
                  href="/kontakt"
                  className="inline-block bg-white text-primary px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                >
                  Jetzt anfragen
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
